import { cn } from '../../lib/cn'
import { formatDifferential, formatToPar } from '../../lib/golfFormat'
import type { RoundSummary } from '../../lib/golfRound'
import { HandicapMovement } from './HandicapMovement'
import { traceColor, traceInitial } from './trace'

export interface RoundSummaryCardPlayer {
  id: string
  /** `round_players.position`, 1-4 — the seat this card's tone comes off. */
  position: number
  displayName: string
  guest?: boolean
  isYou?: boolean
  /** `round_players.index_at_play` — the "before" number of the index story. */
  indexAtPlay?: number | null
  /** `round_players.score_differential`, frozen at completion. */
  scoreDifferential?: number | null
}

export interface RoundSummaryCardProps {
  player: RoundSummaryCardPlayer
  /**
   * `round_player_json`'s `summary`, which is OMITTED rather than nulled when
   * the club computed none — so a card still being written arrives here as
   * `undefined` and says so.
   */
  summary?: RoundSummary | null
  className?: string
}

/**
 * One player's afternoon: the score, the moment, and what it did to their
 * index — in ONE card.
 *
 * ## Why the hero and the moments are not two components
 *
 * They were offered as alternatives and the maintainer took BOTH (decision
 * record §3, 2026-08-22 —
 * `../../../../../../docs/fireside/club-web-decisions.md`): a big score with
 * nothing around it is a scoreboard,
 * and a moments strip with no score is a highlight reel of a round nobody can
 * place. Together they are the thing a member wants to send someone. The index
 * line closes the card because it is the one number that outlives the day.
 *
 * ## Every medal is a field the club actually computed
 *
 * There is no medal here that is not `RoundSummary` read straight off the
 * wire, and one that could not be computed is ABSENT rather than dashed —
 * `putts` is null unless every recorded hole carries putts, `longest_drive` is
 * null on an unmapped course, `fairways` is null where nobody marked one. A
 * chip that said "0 putts" or "— yards" would be the club claiming a
 * measurement it refused to make.
 *
 * What is deliberately NOT here and must not be added without the recorded
 * decision changing: **strokes gained and plays-like**. Private strokes-gained
 * results have their own owner-only read surfaces and must never enter this
 * shared summary; the authorized 2025 benchmark remains unavailable. The
 * plays-like adapter remains outside this card.
 */
export function RoundSummaryCard({ player, summary, className }: RoundSummaryCardProps) {
  const tone = traceColor(player.position)
  const initial = traceInitial(player.displayName)

  return (
    <article
      className={cn(
        'flex flex-col overflow-hidden rounded-club-lg border border-sage-soft bg-parchment',
        className,
      )}
      data-round-summary={player.id}
    >
      <header className="flex items-center gap-2.5 bg-pine px-3.5 py-2.5 text-paper">
        {initial && (
          <span
            aria-hidden="true"
            className="grid size-5 shrink-0 place-items-center rounded-full bg-paper font-display text-[10px]"
            style={{ color: tone }}
          >
            {initial}
          </span>
        )}
        <b className="font-display text-[14.5px] font-medium tracking-[0.01em]">{player.displayName}</b>
        {player.isYou && <HeadChip>You</HeadChip>}
        {player.guest && <HeadChip>Guest</HeadChip>}
      </header>

      {summary ? (
        <Told player={player} summary={summary} />
      ) : (
        <p className="px-3.5 py-4 text-[12.5px] text-ink-soft">
          The club totals a card once the round is closed.
        </p>
      )}
    </article>
  )
}

function Told({ player, summary }: { player: RoundSummaryCardPlayer; summary: RoundSummary }) {
  const moment = momentFor(summary)
  const medals = medalsFor(summary)

  return (
    <>
      <p className="flex flex-wrap items-baseline gap-x-2.5 px-3.5 pb-1 pt-3.5">
        <b className="font-display text-[38px] font-medium leading-none tabular-nums text-pine">
          {summary.gross ?? '—'}
        </b>
        <span className="text-[12.5px] text-ink-soft">{scoreLine(summary, player.scoreDifferential)}</span>
      </p>

      {moment && (
        <div className="mx-3.5 mt-2.5 rounded-club border border-brass bg-paper px-2.5 py-2" data-round-moment="">
          <span className="text-[8.5px] font-semibold uppercase tracking-[0.2em] text-brass-deep">
            The moment
          </span>
          <p className="mt-0.5 text-[13px] font-semibold text-ink">{moment}</p>
        </div>
      )}

      {medals.length > 0 && (
        <ul className="mt-2.5 flex flex-wrap gap-1.5 px-3.5" data-round-medals="">
          {medals.map((medal) => (
            <li
              key={medal.key}
              data-round-medal={medal.key}
              className="rounded-club-sm border border-sage-soft bg-paper px-1.5 py-1 text-[10.5px] text-ink-soft"
            >
              {medal.label}
              <b className="ml-1 font-semibold tabular-nums text-ink">{medal.value}</b>
            </li>
          ))}
        </ul>
      )}

      <HandicapMovement
        indexAtPlay={player.indexAtPlay}
        indexAfter={summary.index_after}
        indexDelta={summary.index_delta}
        className="mx-3.5 mb-3.5 mt-3"
      />
    </>
  )
}

function HeadChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="ml-auto whitespace-nowrap rounded-club-sm border border-gilt/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-gilt">
      {children}
    </span>
  )
}

/**
 * "+12 · net 71 · diff 9.8" — the qualifiers beside the gross, and only the
 * ones the club has.
 *
 * `net` is printed without its own to-par because the to-par printed first is
 * the gross's, and two signed numbers in one line read as a range.
 */
function scoreLine(summary: RoundSummary, differential: number | null | undefined): string {
  const parts: string[] = []
  if (summary.to_par !== null) parts.push(formatToPar(summary.to_par))
  if (summary.net !== null) parts.push(`net ${summary.net}`)
  if (differential !== null && differential !== undefined) {
    parts.push(`diff ${formatDifferential(differential)}`)
  }
  if (parts.length === 0) return `${summary.holes_scored} holes scored`
  return parts.join(' · ')
}

/** What a hole cost against its own par, named the way golf names it. */
const SHAPES: Partial<Record<number, string>> = {
  [-3]: 'An albatross',
  [-2]: 'An eagle',
  [-1]: 'A birdie',
}

/**
 * The round's headline, and the rule is that it must be TRUE before it is
 * good.
 *
 * `best_hole` is the server's own deterministic pick, so an under-par hole is
 * the moment and it is named. Where the best of the day was only a par, that
 * IS the moment — a card with no red on it is a real afternoon. Where even the
 * best hole cost a stroke, the club says so plainly rather than reaching for a
 * flattering adjective; and where there is no best hole at all (an unscored
 * card), there is no moment and no box.
 */
function momentFor(summary: RoundSummary): string | null {
  const best = summary.best_hole
  if (best === null) {
    const pars = summary.shapes?.pars ?? 0
    return pars > 0 ? `${pars} ${pars === 1 ? 'par' : 'pars'} on the card.` : null
  }

  if (best.to_par < 0) {
    // Golf has names for one, two and three under and stops there. A fourth
    // would be a word the club invented, so a hole beyond the table is
    // described by its own two numbers instead.
    const named = SHAPES[best.to_par]
    return named
      ? `${named} on ${best.hole_number} — the round’s best hole.`
      : `${best.strokes} on a par ${best.par} — the round’s best hole.`
  }
  if (best.to_par === 0) return `A par on ${best.hole_number} — the best of the day.`
  return `${best.strokes} on ${best.hole_number}, the day’s closest to par.`
}

interface Medal {
  key: string
  label: string
  value: string
}

/**
 * The chips, in the order a member would brag about them. Each is one field of
 * `RoundSummary`; a null field yields NO chip, which is why this returns a
 * built list rather than a fixed row with holes in it.
 */
function medalsFor(summary: RoundSummary): Medal[] {
  const medals: Medal[] = []

  if (summary.longest_drive) {
    const { yards, club } = summary.longest_drive
    medals.push({
      key: 'longest-drive',
      label: 'Longest drive',
      value: club ? `${yards}y · ${club}` : `${yards}y`,
    })
  }
  if (summary.putts) {
    medals.push({ key: 'putts', label: 'Putts', value: String(summary.putts.total) })
  }
  if (summary.fairways) {
    medals.push({
      key: 'fairways',
      label: 'Fairways',
      value: `${summary.fairways.hit}/${summary.fairways.of}`,
    })
  }
  // A net score UNDER the par of the holes played is the member beating their
  // own handicap, which is the one derived claim on this card — and it is
  // arithmetic on two published numbers, not a model. Level net is not a beat.
  if (summary.net_to_par !== null && summary.net_to_par < 0) {
    medals.push({
      key: 'beat-handicap',
      label: 'Beat handicap by',
      value: String(Math.abs(summary.net_to_par)),
    })
  }

  return medals
}
